import React, { useState, useCallback } from 'react';
import { Link, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import CardList from '../components/cards/card-list';
import { useChangePermissionsMutation, useGetGroupProjectsQuery } from '../services/groups.service';
import { ProjectsBehavior } from '../models/group';
import type { GroupsProject } from '../models/group';
import ConfirmModal from '../components/modals/confirm-modal';
import Search from '../components/search';
import linkTo from '../utils/linkTo';
import { searchSortHeaderStyles } from './__fixtures__';
import { listResourcesFallbackUIProps } from './group-fallback-ui';
import { OptionItem, EventData } from '../components/cards/card.types';

export interface Props {
  groupId: string;
}

const projectsBehavior = new ProjectsBehavior();

export default function GroupsProjects({ groupId }: Props) {
  const [params, setParams] = useState({
    id: groupId,
    page: 1,
    per_page: 100,
    sort: 'name',
    search: '',
  });

  const { data: group, isFetching, isError } = useGetGroupProjectsQuery({ ...params });
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState<GroupsProject[]>([]);
  const [changePermission] = useChangePermissionsMutation();
  const groupProjects = React.useMemo(
    () => [...projects, ...group?.projects ?? []], [group?.projects],
  );
  const showSearch = !!groupProjects.length || (!groupProjects.length && !!params.search.length);
  const isSearching = params.search !== '';
  const [removedProjectName, setRemovedProjectName] = useState<string | undefined>('');

  const handleRemove = () => {
    changePermission({
      type: 'group_projects',
      groupId,
      remove: [projectId],
      item: removedProjectName,
    });
    resetDeleteModal();
  };

  const handleFetchMore = useCallback(() => {
    if (group && params.page !== group.total_pages) {
      setParams({ ...params, page: params.page + 1 });
      setProjects(groupProjects);
    }
  }, [groupProjects]);

  const handleSearch = (search: string) => {
    setProjects([]);
    setParams({ ...params, page: 1, search });
  };
  const removeClick = (id: string, title: string) => {
    setProjectId(id);
    setRemovedProjectName(title);
  };
  const resetDeleteModal = () => setProjectId('');

  const { EmptyImage, emptyText, emptyTextHelper } = listResourcesFallbackUIProps({
    resource: 'projects',
    isSearching,
  });

  const onRemoveEvent = (_dataType: OptionItem, eventData: EventData) => {
    removeClick(eventData.id, eventData.title || '');
  };

  return (
    <>
      <ConfirmModal
        onPositive={handleRemove}
        onNegative={resetDeleteModal}
        positive="Remove"
        title="Remove Project?"
        open={!!projectId}
        maxWidth="xs"
      >
        <Typography mb={2}>{projectsBehavior.getDeleteText()}</Typography>
        <Link target="_blank" href={linkTo.groups.removingAccess} rel="noreferrer">
          Learn more
        </Link>
      </ConfirmModal>
      {showSearch && (
        <Stack sx={{ ...searchSortHeaderStyles }}>
          <Search
            setGlobalFilter={handleSearch}
            placeholder="Search name or description"
          />
        </Stack>
      )}
      <CardList
        hideAvatar
        rows={groupProjects}
        onEvent={onRemoveEvent}
        onFetchMore={handleFetchMore}
        isLoading={isFetching}
        error={isError && 'There was an error fetching projects.'}
        EmptyImage={EmptyImage}
        emptyText={emptyText}
        emptyTextHelper={emptyTextHelper}
        actionItems={[OptionItem.Delete]}
      />
    </>
  );
}
